/* src/components/heroManagementDashboard/aiWorkplace/AIWorkplaceDashboard.js */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../firebase";
import { signOut } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import LoadingCircleSmall from "../../utils/LoadingCircleSmall";
import tpcLogo from "../../../images/TPC-logo-light.png";
import "./AIWorkplaceDashboard.css";

export default function AIWorkplaceDashboard() {
  const reduxUser = useSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate("/ai-workplace-login");
          return;
        }

        const fn = getFunctions();
        const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");

        const result = await readDocSafe({
          company: "TPC_Group",
          collectionBrand: "TechPerformanceCentre",
          systemName: "AIWorkplaceUsers",
          uniqueId: currentUser.email,
        });

        if (result.data?.found) {
          setProfileData(result.data.data);

          // If profile is not complete, redirect to welcome page
          if (!result.data.data.isCompleted) {
            navigate("/ai-workplace-welcome");
          }
        } else {
          // No profile found, redirect to welcome
          navigate("/ai-workplace-welcome");
        }
      } catch (err) {
        console.error("[AIWorkplaceDashboard] Error fetching profile:", err);
        setError("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatFridayDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    const friday = new Date(date);
    friday.setDate(friday.getDate() + 1);
    return friday.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  // Determine application status
  const getApplicationStatus = () => {
    if (!profileData) return "Unknown";
    if (profileData.status === "completed" || profileData.isCompleted) {
      return "Under Review";
    }
    return "Incomplete";
  };

  const getStatusBadgeClass = () => {
    const status = getApplicationStatus();
    if (status === "Under Review") return "status-review";
    if (status === "Incomplete") return "status-incomplete";
    return "status-default";
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/ai-workplace-login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loading) {
    return <LoadingCircleSmall label="Loading your dashboard..." />;
  }

  if (error) {
    return (
      <div className="aiwd-error-container">
        <p>{error}</p>
        <button onClick={() => navigate("/ai-workplace-login")}>
          Back to Login
        </button>
      </div>
    );
  }

  if (!profileData) {
    return null;
  }

  return (
    <div className="aiwd-container">
      {/* Header */}
      <div className="aiwd-header">
        <div className="aiwd-header-top">
          <img src={tpcLogo} alt="TPC" className="aiwd-logo" />
          <button onClick={handleLogout} className="aiwd-logout-btn">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Log Out
          </button>
        </div>
        <h1 className="aiwd-title">AI in the Workplace Dashboard</h1>
        <p className="aiwd-subtitle">
          Welcome back,{" "}
          {profileData.fullName || profileData.name || "Professional"}!
        </p>
      </div>

      {/* Main Content */}
      <div className="aiwd-content">
        {/* Application Status Card */}
        <div className="aiwd-status-card">
          <h2>Application Status</h2>
          <div className="aiwd-status-badge-container">
            <span className={`aiwd-status-badge ${getStatusBadgeClass()}`}>
              {getApplicationStatus()}
            </span>
          </div>
          <p className="aiwd-status-message">
            {getApplicationStatus() === "Under Review"
              ? "Your application is currently under review. Our team will contact you within 2-3 business days."
              : "Please complete your application to proceed."}
          </p>
        </div>

        {/* Personal Information */}
        <div className="aiwd-info-section">
          <h2>Personal Information</h2>
          <div className="aiwd-info-grid">
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Full Name</span>
              <span className="aiwd-info-value">
                {profileData.fullName || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Email Address</span>
              <span className="aiwd-info-value">
                {profileData.email || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Phone Number</span>
              <span className="aiwd-info-value">
                {profileData.phone || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Country</span>
              <span className="aiwd-info-value">
                {profileData.country || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Age Bracket</span>
              <span className="aiwd-info-value">
                {profileData.ageBracket || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Gender</span>
              <span className="aiwd-info-value">
                {profileData.gender || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="aiwd-info-section">
          <h2>Professional Information</h2>
          <div className="aiwd-info-grid">
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Company/Organization</span>
              <span className="aiwd-info-value">
                {profileData.company || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Employment Status</span>
              <span className="aiwd-info-value">
                {profileData.employmentStatus || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Industry Sector</span>
              <span className="aiwd-info-value">
                {profileData.workplaceSector || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Application Type</span>
              <span className="aiwd-info-value">
                {profileData.applicationType || "N/A"}
                {profileData.applicationType === "Team" &&
                  profileData.teamMemberCount &&
                  ` (${profileData.teamMemberCount} members)`}
              </span>
            </div>
          </div>
        </div>

        {/* AI Experience */}
        <div className="aiwd-info-section">
          <h2>AI Experience & Goals</h2>
          <div className="aiwd-info-grid">
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">AI Experience Level</span>
              <span className="aiwd-info-value">
                {profileData.aiExperienceLevel || "N/A"}
              </span>
            </div>
            {profileData.currentAITools &&
              profileData.currentAITools.length > 0 && (
                <div className="aiwd-info-item aiwd-full-width">
                  <span className="aiwd-info-label">Current AI Tools</span>
                  <span className="aiwd-info-value">
                    {profileData.currentAITools.join(", ")}
                  </span>
                </div>
              )}
            {profileData.aiGoals && profileData.aiGoals.length > 0 && (
              <div className="aiwd-info-item aiwd-full-width">
                <span className="aiwd-info-label">AI Goals</span>
                <span className="aiwd-info-value">
                  {profileData.aiGoals.join(", ")}
                </span>
              </div>
            )}
            {profileData.aiChallenges && (
              <div className="aiwd-info-item aiwd-full-width">
                <span className="aiwd-info-label">AI Challenges</span>
                <span className="aiwd-info-value">
                  {profileData.aiChallenges}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Enrollment Information */}
        <div className="aiwd-info-section">
          <h2>Enrollment Information</h2>
          <div className="aiwd-info-grid">
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Enrollment Date</span>
              <span className="aiwd-info-value">
                {profileData.selectedThursdays &&
                profileData.selectedThursdays.length > 0 ? (
                  <>
                    {formatDate(profileData.selectedThursdays[0])}
                    <br />
                    <small style={{ opacity: 0.8 }}>
                      & {formatFridayDate(profileData.selectedThursdays[0])}
                    </small>
                  </>
                ) : (
                  "Not selected"
                )}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Date Applied</span>
              <span className="aiwd-info-value">
                {profileData.dateAdded || "N/A"}
              </span>
            </div>
            <div className="aiwd-info-item">
              <span className="aiwd-info-label">Last Updated</span>
              <span className="aiwd-info-value">
                {profileData.lastUpdated
                  ? new Date(profileData.lastUpdated).toLocaleDateString(
                      "en-US"
                    )
                  : "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="aiwd-contact-card">
          <h3>Need Assistance?</h3>
          <p>
            If you have any questions about your application or training
            schedule, please contact us:
          </p>
          <a
            href="mailto:registrations@tpc.training"
            className="aiwd-contact-email"
          >
            registrations@tpc.training
          </a>
        </div>
      </div>
    </div>
  );
}
